/**
 * DEMA-WMA Gainer backtest (Backtest tab): 1-minute BANKNIFTY / NIFTY index candles (1-minute versions) or
 * stored 5-minute bars, RSI Momentum pattern: From / To / P&L model, any saved version, every run stored and
 * reopenable. 1-minute runs choose the candle source (Auto / Live feed only / Breeze only -- one source per
 * session, never mixed) and list every session's source and missing minutes.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  type DemaWmaBacktestResult,
  type DemaWmaSessionReport,
  type DemaWmaSignalSource,
  type DemaWmaBarSource,
  type DemaWmaBacktestRunSummary,
  type DemaWmaBucket,
  type DemaWmaLagStats,
  type DemaWmaPnlModel,
  type DemaWmaPriceQuality,
} from "@/services/demaWmaGainerService";
import { useDemaWmaService } from "@/services/demaWmaServiceContext";
import ReferenceDataNote from "@/components/backtest/ReferenceDataNote";
import { extractErrorMessage } from "@/utils/errors";

const num = (v: number | null | undefined, d = 2) => (v === null || v === undefined ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d }));
const money = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `₹${num(v)}`);
const tone = (v: number | null | undefined) => (v === null || v === undefined ? undefined : v >= 0 ? "success.main" : "error.main");
const hm = (iso: string | null) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");
const ist = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle1" fontWeight={600} sx={{ color }}>{value}</Typography>
    </Paper>
  );
}

function Buckets({ title, rows }: { title: string; rows?: Record<string, DemaWmaBucket> }) {
  if (!rows || Object.keys(rows).length === 0) return null;
  return (
    <Grid item xs={12} md={4}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Table size="small">
        <TableHead><TableRow><TableCell /><TableCell>Trades</TableCell><TableCell>Win %</TableCell><TableCell>Net</TableCell></TableRow></TableHead>
        <TableBody>
          {Object.entries(rows).map(([k, b]) => (
            <TableRow key={k}>
              <TableCell>{k}</TableCell><TableCell>{b.trades}</TableCell><TableCell>{num(b.win_rate_pct, 1)}</TableCell>
              <TableCell sx={{ color: tone(b.net_pnl) }}>{money(b.net_pnl)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Grid>
  );
}

/** " [live 12, Breeze 30 sessions]" for 1-minute-based runs; "" otherwise. */
export function sourceText(bySource: unknown): string {
  if (!bySource || typeof bySource !== "object") return "";
  const m = bySource as Record<string, number>;
  return ` [live ${m.LIVE ?? 0}, Breeze ${m.BREEZE ?? 0} sessions]`;
}

export function tradesCsv(r: DemaWmaBacktestResult): string {
  const cols = ["index", "side", "strike", "expiry", "quantity", "signal_bar_end", "entry_time", "entry_underlying", "entry_premium",
    "stop_loss", "target", "exit_time", "exit_underlying", "exit_premium", "exit_reason", "gross_pnl", "charges", "net_pnl", "bars_held",
    "entry_price_lag_s", "exit_price_lag_s", "exit_fill_basis", "entry_price_source", "exit_price_source"] as const;
  const lines = [cols.join(",")];
  for (const t of r.trades) lines.push(cols.map((c) => String(t[c] ?? "")).join(","));
  return lines.join("\n");
}

/** Price source of 1-minute option candles (trade `entry_price_source` / `exit_price_source`). */
const MINUTE_PRICE_SOURCE = "BREEZE_HISTORICAL_1MIN";

const PNL_MODEL_SHORT: Record<string, string> = { OPTION_DELTA: "delta", OPTION_CHAIN: "chain", OPTION_1MIN: "1-min" };

/** OPTION_1MIN trade price source: "1-min", "5-min fallback" or "1-min / 5-min fallback" (entry / exit). */
function priceSourceText(entry?: string | null, exit?: string | null): string {
  const one = (s?: string | null) => (!s ? "—" : s === MINUTE_PRICE_SOURCE ? "1-min" : "5-min fallback");
  const a = one(entry), b = one(exit);
  return a === b ? a : `${a} / ${b}`;
}

function PriceQualityNote({ q }: { q: DemaWmaPriceQuality }) {
  const lag = (l: DemaWmaLagStats) => (l.n ? `median ${l.median_s}s, max ${l.max_s}s` : "none");
  const basis = Object.entries(q.exit_fill_basis).map(([k, v]) => `${k} ${v}`).join(", ");
  const unpriced = q.entries_unpriceable;
  return (
    <Alert severity={unpriced > 0 || q.exit_lag_bar_close_exits.over_300s > 0 ? "warning" : "info"} sx={{ mb: 1 }}
           data-testid="price-quality">
      Option prices: entries filled at the first stored price after the signal ({lag(q.entry_lag)}; max allowed{" "}
      {q.max_entry_lag_seconds}s) · {unpriced} signal(s) skipped as unpriceable · bar-close exits priced{" "}
      {lag(q.exit_lag_bar_close_exits)} after the decision ({q.exit_lag_bar_close_exits.over_300s} over 5 min) · exit
      fills: {basis || "none"}.{" "}
      {q.fills_1min !== undefined
        ? `1-minute option candles priced ${q.fills_1min} fill(s); ${q.fills_5min_fallback ?? 0} fell back to ~5-minute prices.`
        : "Stored prices are ~5-minutely: moves between them are not seen."}
    </Alert>
  );
}

export const SIGNAL_SOURCE_LABEL: Record<DemaWmaSignalSource, string> = {
  AUTO: "Auto: live feed, else Breeze (per session)",
  LIVE: "Live feed only",
  BREEZE_HISTORICAL: "Breeze historical only",
};

type SessionStatus = "OK" | "GAPS" | "INCOMPLETE" | "NO DATA" | "CLOSED DAY";
const STATUS_COLOR: Record<SessionStatus, "success" | "warning" | "error" | "default"> = {
  OK: "success", GAPS: "warning", INCOMPLETE: "error", "NO DATA": "error", "CLOSED DAY": "default",
};

export function sessionStatus(s: DemaWmaSessionReport, tolerance: number): SessionStatus {
  if (!s.trading_day) return "CLOSED DAY";
  if (!s.bars) return "NO DATA";
  const miss = Number(s.missing_minutes ?? 0);
  if (miss > tolerance) return "INCOMPLETE";
  return miss > 0 ? "GAPS" : "OK";
}

/** Every session of a 1-minute run: the one source it used and its missing minutes. Problem sessions first;
 *  "Show all" lists the clean ones too. */
export function SessionQuality({ bt }: { bt: DemaWmaBacktestResult }) {
  const [all, setAll] = useState(false);
  const rows = Object.entries(bt.data_quality ?? {}).flatMap(([ix, q]) => {
    const list = (q as { sessions?: DemaWmaSessionReport[] }).sessions;
    const tol = Number((q as { max_missing_minutes_per_session?: number }).max_missing_minutes_per_session ?? 3);
    return Array.isArray(list) ? list.map((s) => ({ ix, s, st: sessionStatus(s, tol) })) : [];
  });
  if (rows.length === 0) return null;
  const problems = rows.filter((r) => r.st !== "OK" && r.st !== "CLOSED DAY");
  const shown = all ? rows : problems;
  const srcName = (src?: string | null) => (src === "LIVE" ? "Live feed" : src === "BREEZE" ? "Breeze" : "—");
  return (
    <Box sx={{ mb: 2 }} data-testid="session-quality">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">Session data</Typography>
        <Chip size="small" label={`${rows.length} session(s)`} />
        {problems.length > 0
          ? <Chip size="small" color="warning" label={`${problems.length} with missing data`} data-testid="session-problems" />
          : <Chip size="small" color="success" label="no missing data" />}
        <Button size="small" onClick={() => setAll((a) => !a)}>{all ? "Show problems only" : "Show all sessions"}</Button>
      </Stack>
      {shown.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Index</TableCell><TableCell>Role</TableCell><TableCell>Source</TableCell>
                <TableCell>Bars</TableCell><TableCell>Missing min.</TableCell><TableCell>Missing ranges</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(({ ix, s, st }) => (
                <TableRow key={`${ix}-${s.date}-${s.role}`}>
                  <TableCell>{s.date}</TableCell><TableCell>{ix}</TableCell><TableCell>{s.role}</TableCell>
                  <TableCell>{srcName(s.source)}</TableCell><TableCell>{s.bars ?? 0}</TableCell>
                  <TableCell>{s.trading_day ? s.missing_minutes ?? 0 : "—"}</TableCell>
                  <TableCell><Typography variant="caption">{(s.missing_ranges ?? []).slice(0, 6).join(", ") || "—"}</Typography></TableCell>
                  <TableCell><Chip size="small" color={STATUS_COLOR[st]} label={st} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Each session uses ONE candle source (never mixed). GAPS = within the missing-minute tolerance (the bar after a gap never
        opens a trade); INCOMPLETE = over the tolerance, so a run including it is refused.
      </Typography>
    </Box>
  );
}

export interface BacktestVersionOption { version: number; is_active: boolean; rule_set?: string; timeframe_minutes?: number }

export function DemaWmaBacktestCard({ timeframeMinutes = 5, versions = [] }: { timeframeMinutes?: number; versions?: BacktestVersionOption[] } = {}) {
  const demaWmaGainerService = useDemaWmaService();
  const [versionSel, setVersionSel] = useState<string>("");          // "" = the active version
  const picked = versions.find((v) => String(v.version) === versionSel);
  const oneMinute = (picked ? Number(picked.timeframe_minutes ?? 5) : timeframeMinutes) === 1;
  const [barSource, setBarSource] = useState<DemaWmaBarSource>("NATIVE");
  const built = !oneMinute && barSource === "FROM_1MIN";
  const usesMinuteData = oneMinute || built;
  const [maxMissing, setMaxMissing] = useState("3");
  const [signalSource, setSignalSource] = useState<DemaWmaSignalSource>("AUTO");
  const [refMode, setRefMode] = useState<"DATED" | "CURRENT">("DATED");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [model, setModel] = useState<DemaWmaPnlModel>("OPTION_DELTA");
  const [delta, setDelta] = useState("0.5");
  const [premBn, setPremBn] = useState("400");
  const [premNf, setPremNf] = useState("120");
  const [maxLag, setMaxLag] = useState("60");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [bt, setBt] = useState<DemaWmaBacktestResult | null>(null);
  const [runs, setRuns] = useState<DemaWmaBacktestRunSummary[] | null>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      setBt(await demaWmaGainerService.runBacktest({
        from_date: from, to_date: to, pnl_model: model,
        ...(model === "OPTION_DELTA" ? { option_delta: Number(delta), assumed_premium_banknifty: Number(premBn), assumed_premium_nifty: Number(premNf) } : {}),
        ...(model !== "OPTION_DELTA" ? { chain_max_lag_seconds: Number(maxLag) } : {}),
        ...(usesMinuteData ? { max_missing_minutes_per_session: Number(maxMissing) } : {}),
        ...(built ? { bar_source: "FROM_1MIN" as const } : {}),
        ...(usesMinuteData && signalSource !== "AUTO" ? { signal_source: signalSource } : {}),
        ...(refMode === "CURRENT" ? { reference_data: "CURRENT" as const } : {}),
        ...(picked ? { config_version: picked.version } : {}),
      }));
      setRuns(null);
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const loadRuns = async () => {
    try {
      setRuns(await demaWmaGainerService.listBacktestRuns(50));
    } catch (e) {
      setErr(extractErrorMessage(e));
    }
  };

  const openRun = async (id: string) => {
    if (!id) return;
    setBusy(true);
    setErr(null);
    try {
      setBt(await demaWmaGainerService.getBacktestRun(id));
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!bt) return;
    const g = globalThis;
    const url = g.URL.createObjectURL(new g.Blob([tradesCsv(bt)], { type: "text/csv" }));
    const a = g.document.createElement("a");
    a.href = url;
    a.download = `dema_wma_backtest_${bt.from_date ?? from}_${bt.to_date ?? to}.csv`;
    a.click();
    g.URL.revokeObjectURL(url);
  };

  const m = bt?.metrics;
  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="backtest-card">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {oneMinute
            ? "Backtest (historical 1-minute BANKNIFTY / NIFTY index candles: live feed or Breeze history, one source per session)"
            : "Backtest (historical 5-minute BANKNIFTY / NIFTY from stored OI-analysis data)"}
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField label="From" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          {versions.length > 0 && (
            <TextField select label="Version" size="small" value={versionSel} sx={{ minWidth: 200 }}
              onChange={(e) => setVersionSel(e.target.value)} helperText="Backtest any saved version">
              <MenuItem value="">Active version</MenuItem>
              {versions.map((v) => (
                <MenuItem key={v.version} value={String(v.version)}>
                  v{v.version}{v.is_active ? " (active)" : ""}{v.timeframe_minutes === 1 ? " · 1-min" : ""}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField select label="P&L model" size="small" value={model} onChange={(e) => setModel(e.target.value as DemaWmaPnlModel)} sx={{ minWidth: 220 }}>
            <MenuItem value="OPTION_DELTA">Option (delta approx.)</MenuItem>
            <MenuItem value="OPTION_CHAIN">Option (stored chain prices)</MenuItem>
            <MenuItem value="OPTION_1MIN">Option 1-minute (real prices)</MenuItem>
          </TextField>
          {model !== "OPTION_DELTA" && (
            <TextField label="Max price lag (s)" size="small" type="number" value={maxLag} sx={{ width: 150 }}
              onChange={(e) => setMaxLag(e.target.value)} inputProps={{ min: 0, max: 600, "data-testid": "max-lag" }}
              helperText="Entry = first stored price after the signal" />
          )}
          {model === "OPTION_DELTA" && (
            <>
              <TextField label="Delta" size="small" type="number" value={delta} onChange={(e) => setDelta(e.target.value)} sx={{ width: 100 }} />
              <TextField label="BANKNIFTY premium" size="small" type="number" value={premBn} onChange={(e) => setPremBn(e.target.value)} sx={{ width: 160 }} />
              <TextField label="NIFTY premium" size="small" type="number" value={premNf} onChange={(e) => setPremNf(e.target.value)} sx={{ width: 140 }} />
            </>
          )}
          {!oneMinute && (
            <TextField select label="Bars" size="small" value={barSource} sx={{ minWidth: 250 }}
              onChange={(e) => setBarSource(e.target.value as DemaWmaBarSource)}>
              <MenuItem value="NATIVE">Stored 5-minute bars (default)</MenuItem>
              <MenuItem value="FROM_1MIN">Built from 1-minute candles</MenuItem>
            </TextField>
          )}
          <TextField select label="Reference data" size="small" value={refMode} sx={{ minWidth: 230 }}
            onChange={(e) => setRefMode(e.target.value as "DATED" | "CURRENT")}
            helperText="Lot size, expiry day, charges">
            <MenuItem value="DATED">As of each trade date (default)</MenuItem>
            <MenuItem value="CURRENT">Today&apos;s values</MenuItem>
          </TextField>
          {usesMinuteData && (
            <TextField select label="Data source" size="small" value={signalSource} sx={{ minWidth: 260 }}
              onChange={(e) => setSignalSource(e.target.value as DemaWmaSignalSource)}
              helperText="1-minute index candles; one source per session" inputProps={{ "data-testid": "signal-source" }}>
              {(Object.keys(SIGNAL_SOURCE_LABEL) as DemaWmaSignalSource[]).map((k) => (
                <MenuItem key={k} value={k}>{SIGNAL_SOURCE_LABEL[k]}</MenuItem>
              ))}
            </TextField>
          )}
          {usesMinuteData && (
            <TextField label="Max missing min / session" size="small" type="number" value={maxMissing}
              onChange={(e) => setMaxMissing(e.target.value)} inputProps={{ min: 0, max: 15 }} sx={{ width: 190 }}
              helperText="A session with more is a data gap; the bar after a gap never opens a trade" />
          )}
          <Button variant="contained" disabled={!from || !to || busy || (usesMinuteData && !(Number(maxMissing) >= 0 && Number(maxMissing) <= 15 && maxMissing !== ""))} onClick={() => void run()} title="Uses the active (saved) config"
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {busy ? "Running…" : "Run backtest"}
          </Button>
          <TextField select size="small" label="Stored runs" value="" sx={{ minWidth: 280 }}
            SelectProps={{ onOpen: () => void loadRuns() }} onChange={(e) => void openRun(e.target.value)}
            helperText="Reopen an earlier backtest" inputProps={{ "aria-label": "Stored runs" }}>
            {runs === null && <MenuItem value="" disabled>Loading…</MenuItem>}
            {runs?.length === 0 && <MenuItem value="" disabled>No stored runs yet</MenuItem>}
            {(runs ?? []).map((r) => (
              <MenuItem key={r.backtest_id} value={r.backtest_id}>
                {ist(r.created_at)} · {r.from_date}→{r.to_date} · {PNL_MODEL_SHORT[r.pnl_model ?? ""] ?? "delta"} · {r.trade_count} trades
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {busy && <LinearProgress sx={{ mt: 2 }} />}
        {err && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErr(null)} data-testid="backtest-error">{err}</Alert>}

        {!bt && !busy && !err && (
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }} data-testid="backtest-empty">
            Pick a date range and run a backtest, or reopen a stored run. Results show the data source, trades, P&amp;L and
            every session&apos;s data quality.
          </Alert>
        )}
        {bt && (
          <Box sx={{ mt: 2 }} data-testid="backtest-result">
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`Config v${bt.config_version ?? "—"} (active when run)`} />
              <Chip size="small" label={String(bt.settings?.pnl_model ?? "")} />
              {bt.from_date && <Chip size="small" label={`${bt.from_date} → ${bt.to_date}`} />}
              {bt.created_at && <Chip size="small" variant="outlined" label={`Run ${ist(bt.created_at)}`} />}
              {(bt.timeframe_minutes === 1 || bt.bar_source === "FROM_1MIN") && (
                <Chip size="small" color="info" variant="outlined" data-testid="run-source"
                      label={`Source: ${SIGNAL_SOURCE_LABEL[bt.signal_source ?? "AUTO"]}`} />
              )}
            </Stack>
            {bt.note && <Alert severity="info" sx={{ mb: 1 }}>{bt.note}</Alert>}
            {m && m.total_trades ? (
              <>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={2}><Metric label="Trades" value={String(m.total_trades)} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Win rate" value={`${num(m.win_rate_pct, 1)}%`} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Net P&L" value={money(m.net_pnl)} color={tone(m.net_pnl)} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Charges" value={money(m.charges)} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Max drawdown" value={money(m.max_drawdown)} color="error.main" /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Profit factor" value={num(m.profit_factor)} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Avg win" value={money(m.avg_win)} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Avg loss" value={money(m.avg_loss)} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Gross P&L" value={money(m.gross_pnl)} color={tone(m.gross_pnl)} /></Grid>
                  <Grid item xs={6} md={2}><Metric label="Avg bars held" value={num(m.avg_bars_held, 1)} /></Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Buckets title="By index" rows={m.by_index} />
                  <Buckets title="By side" rows={m.by_side} />
                  <Buckets title="By exit reason" rows={m.by_exit_reason} />
                </Grid>
              </>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>No trades in this range.</Alert>
            )}
            {Object.keys(bt.rejections ?? {}).length > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Signals not traded: {Object.entries(bt.rejections).map(([k, v]) => `${k} ${v}`).join(", ")}
              </Typography>
            )}
            {bt.price_quality && <PriceQualityNote q={bt.price_quality} />}
            {!bt.price_quality && bt.settings?.pnl_model === "OPTION_CHAIN" && (
              <Alert severity="warning" sx={{ mb: 1 }} data-testid="price-quality-legacy">
                Run stored before the option-pricing fixes (2026-10-26): prices up to 6 minutes OLD were used and
                stop-losses only saw snapshots, so results are likely too optimistic. Re-run it.
              </Alert>
            )}
            <Typography variant="body2" sx={{ mb: 1 }} data-testid="backtest-quality">
              {bt.bar_source === "FROM_1MIN" ? "Bars built from 1-minute candles. " : ""}
              Data: {Object.entries(bt.data_quality ?? {}).map(([ix, q]) =>
                q.error ? `${ix}: ${String(q.error)}` : `${ix} ${q.sessions_in_range} session(s), ${q.bars_in_range} bars (+${q.warmup_bars_before_range} warm-up)${sourceText(q.sessions_by_source)}`).join(" · ")}
            </Typography>
            <SessionQuality bt={bt} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{bt.assumptions}</Typography>
            <Box sx={{ mb: 2 }}><ReferenceDataNote reference={bt.reference_data} /></Box>

            {bt.trades.length > 0 && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Trades ({bt.trades.length})</Typography>
                  <Button size="small" onClick={download}>Download CSV</Button>
                </Stack>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Entry</TableCell><TableCell>Index</TableCell><TableCell>Contract</TableCell><TableCell>Qty</TableCell>
                        <TableCell>Entry prem.</TableCell><TableCell>SL / Target</TableCell><TableCell>Exit</TableCell><TableCell>Exit prem.</TableCell>
                        <TableCell>Reason</TableCell><TableCell>Net</TableCell>
                        {bt.settings?.pnl_model === "OPTION_1MIN" && <TableCell>Price source</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bt.trades.map((t, i) => (
                        <TableRow key={i}>
                          <TableCell>{hm(t.entry_time)}</TableCell><TableCell>{t.index}</TableCell>
                          <TableCell>{t.side} {num(t.strike, 0)} {t.expiry}</TableCell><TableCell>{t.quantity}</TableCell>
                          <TableCell>{num(t.entry_premium)}</TableCell><TableCell>{num(t.stop_loss)} / {num(t.target)}</TableCell>
                          <TableCell>{hm(t.exit_time)}</TableCell><TableCell>{num(t.exit_premium)}</TableCell>
                          <TableCell>{t.exit_reason}</TableCell>
                          <TableCell sx={{ color: tone(t.net_pnl), fontWeight: 600 }}>{money(t.net_pnl)}</TableCell>
                          {bt.settings?.pnl_model === "OPTION_1MIN" && (
                            <TableCell>{priceSourceText(t.entry_price_source, t.exit_price_source)}</TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default DemaWmaBacktestCard;
